// M004-F0646..F0649 / T-M004-0085 - Core's price-plan WIRE, normalised for the
// UI. Everything here is derived from what Core 7fa446e2's real serializers
// emit (src/testSupport/corePriceFixtures/core-wire.json), not from the design:
//
//   * DATE columns (assignment effective_from / effective_to, plan dates) leave
//     Core as JSON timestamps "2026-10-03T00:00:00.000Z" (mysql2 Date -> JSON).
//     The UI works in calendar days "YYYY-MM-DD" and Core's request schemas
//     accept only that form, so ingest cuts the day off. Core runs in UTC
//     (production `timedatectl` = Etc/UTC), where the cut is the calendar day.
//   * TIME columns (zone/window start_local / end_local) leave Core as
//     "10:00:00"; the UI shows and edits "10:00" (Core accepts both on write).
//   * `ledger_state` is an object {code, posting_id, locks_changes, message}.
//   * plan.zones[] rows carry `inherited` (true = the zone belongs to the
//     template plan, not to this plan).
// Pure functions, no network, no React.

const DAY = /^(\d{4}-\d{2}-\d{2})(?:$|[T ])/;
const TIME = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

/** "2026-10-03T00:00:00.000Z" | "2026-10-03" -> "2026-10-03"; anything else is returned untouched (null stays null). */
export const toCalendarDay = (value) => {
  if (typeof value !== 'string') return value;
  const match = DAY.exec(value);
  return match ? match[1] : value;
};

/** "10:00:00" | "10:00" -> "10:00"; "24:00:00" -> "24:00". */
export const toHhmm = (value) => {
  if (typeof value !== 'string') return value;
  const match = TIME.exec(value);
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : value;
};

export const normaliseZone = (zone) => (zone && typeof zone === 'object' ? {
  ...zone,
  start_local: toHhmm(zone.start_local),
  end_local: toHhmm(zone.end_local),
  inherited: zone.inherited === true,
} : zone);

/** A GET /plans row (or an assignment's `plan`): zones normalised. */
export const normalisePlan = (plan) => (plan && typeof plan === 'object' ? {
  ...plan,
  ...(Array.isArray(plan.zones) ? { zones: plan.zones.map(normaliseZone) } : {}),
  ...('effective_from' in plan ? { effective_from: toCalendarDay(plan.effective_from) } : {}),
  ...('effective_to' in plan ? { effective_to: toCalendarDay(plan.effective_to) } : {}),
} : plan);

/** One GET assignments?user_id row. `redundant` stays true | false | null (null = not judged). */
export const normaliseAssignment = (assignment) => (assignment && typeof assignment === 'object' ? {
  ...assignment,
  effective_from: toCalendarDay(assignment.effective_from),
  effective_to: toCalendarDay(assignment.effective_to),
  redundant: assignment.redundant === true ? true : (assignment.redundant === false ? false : null),
  plan: assignment.plan ? normalisePlan(assignment.plan) : assignment.plan,
} : assignment);

export const LEDGER_CODES = ['none', 'awaiting_approval', 'approved', 'dispatched', 'invoiced'];

/**
 * Core's ledger_state object -> {code, locks, message, postingId}. A bare
 * string (older shape) is read as the code. `locks` is Core's own
 * `locks_changes`; a state this code does not know never locks by guess, but
 * `dispatched`/`invoiced`/`posted` always do.
 */
export const normaliseLedgerState = (raw) => {
  if (raw && typeof raw === 'object') {
    const code = typeof raw.code === 'string' ? raw.code : 'none';
    return {
      code,
      locks: raw.locks_changes === true || ['dispatched', 'invoiced', 'posted'].includes(code),
      message: typeof raw.message === 'string' && raw.message ? raw.message : '',
      postingId: raw.posting_id ?? null,
      // Core's ledger_state {…, hand_off_id (uuid|null), user_id (int|null)} (ML001-F0123)
      // names the Ledger invoice-line history read; null/absent -> null.
      handOffId: typeof raw.hand_off_id === 'string' && raw.hand_off_id ? raw.hand_off_id : null,
      userId: Number.isInteger(raw.user_id) ? raw.user_id : null,
      // Core #311 (W-N9): the stage truth of this TX, additive; null/absent on an older Core.
      stage: typeof raw.stage === 'string' && raw.stage ? raw.stage : null,
      stageDetail: typeof raw.stage_detail === 'string' && raw.stage_detail ? raw.stage_detail : null,
      nextAction: typeof raw.next_action === 'string' && raw.next_action ? raw.next_action : null,
    };
  }
  const code = typeof raw === 'string' && raw ? raw : 'none';
  return { code, locks: ['dispatched', 'invoiced', 'posted'].includes(code), message: '', postingId: null, handOffId: null, userId: null, stage: null, stageDetail: null, nextAction: null };
};

/** A set_by that is {id, display_name} | name | id | null -> a display name or ''. */
export const actorLabel = (raw) => {
  if (raw === null || raw === undefined || raw === '') return '';
  if (typeof raw === 'object') return raw.display_name || raw.name || (raw.id ? `User #${raw.id}` : '');
  if (typeof raw === 'number') return `User #${raw}`;
  return String(raw);
};

/** Sum of the `propagation` blocks Core returns from zone writes / dry runs. null blocks (nothing to reprice) count as zero. */
export const sumPropagation = (blocks) => (blocks || []).reduce((total, block) => ({
  toReprice: total.toReprice + (block?.to_reprice || 0),
  held: total.held + (block?.held || 0),
  refusedBilled: total.refusedBilled + (block?.refused_billed || 0),
}), { toReprice: 0, held: 0, refusedBilled: 0 });

/** The calendar day Core compares an occurrence on: the UTC day of its start instant (Core runs in UTC). */
export const occurrenceDateOf = (start) => {
  const date = start instanceof Date ? start : new Date(start);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};
